import * as React from 'react';
import { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';

// --- MUI Imports ---
import AppBar from '@mui/material/AppBar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import MuiToolbar from '@mui/material/Toolbar';
import { tabsClasses } from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';

// --- Custom Component Imports ---
import SideMenuMobile from './SideMenuMobile';
import MenuButton from './MenuButton';
import ColorModeIconDropdown from '../shared-theme/ColorModeIconDropdown';
import { authFetch } from '../authFetch';

const Toolbar = styled(MuiToolbar)({
  width: '100%',
  padding: '12px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'start',
  justifyContent: 'center',
  gap: '12px',
  flexShrink: 0,
  [`& ${tabsClasses.flexContainer}`]: {
    gap: '8px',
    p: '8px',
    pb: 0,
  },
});

const logo = '/logo.svg';

export default function AppNavbar() {
  // --- State for Mobile Drawer ---
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  // --- State and Logic for Notifications (from Header.js) ---
  const [notifications, setNotifications] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const notificationsMenuOpen = Boolean(anchorEl);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await authFetch('/api/notifications/');
        const data = await response.json();
        setNotifications(data);
      } catch (error) {
        console.error("Failed to fetch notifications:", error.message);
      }
    };
    fetchNotifications();
  }, []);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAsRead = async (notificationId) => {
    setNotifications(notifications.map(n =>
      n.id === notificationId ? { ...n, is_read: true } : n
    ));
    try {
      await authFetch(`/api/notifications/${notificationId}/mark_as_read/`, {
        method: 'POST',
      });
    } catch (error) {
      console.error("Failed to mark notification as read:", error.message);
      setNotifications(notifications.map(n =>
        n.id === notificationId ? { ...n, is_read: false } : n
      ));
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const toggleDrawer = (newOpen) => () => {
    setDrawerOpen(newOpen);
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          display: { xs: 'auto', md: 'none' },
          boxShadow: 0,
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          borderBottom: '1px solid',
          borderColor: 'divider',
          top: 'var(--template-frame-height, 0px)',
        }}
      >
        <Toolbar variant="regular">
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              flexGrow: 1,
              width: '100%',
              gap: 1,
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: 'center', mr: 'auto' }}
            >
              <img src={logo} alt="Logo" style={{ height: '32px' }} />
              <Typography variant="h6" component="h1" sx={{ color: 'text.primary', fontSize: '1.1rem' }}>
                Infrastructure Dashboard
              </Typography>
            </Stack>
            <ColorModeIconDropdown />
            <MenuButton
              aria-label="Open notifications"
              aria-controls={notificationsMenuOpen ? 'notifications-menu-mobile' : undefined}
              aria-haspopup="true"
              aria-expanded={notificationsMenuOpen ? 'true' : undefined}
              onClick={handleMenuOpen}
            >
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsRoundedIcon />
              </Badge>
            </MenuButton>
            <MenuButton aria-label="menu" onClick={toggleDrawer(true)}>
              <MenuRoundedIcon />
            </MenuButton>
            <SideMenuMobile open={drawerOpen} toggleDrawer={toggleDrawer} />
          </Stack>
        </Toolbar>
      </AppBar>

      {/* --- NOTIFICATION DROPDOWN MENU --- */}
      <Menu
        id="notifications-menu-mobile"
        anchorEl={anchorEl}
        open={notificationsMenuOpen}
        onClose={handleMenuClose}
        MenuListProps={{
          'aria-labelledby': 'notifications-button',
        }}
        sx={{ mt: 1 }}
      >
        {notifications.length > 0 ? (
          notifications.map((notification) => (
            <MenuItem
              key={notification.id}
              onClick={() => handleMarkAsRead(notification.id)}
              sx={{ fontWeight: notification.is_read ? 'normal' : 'bold' }}
            >
              <Stack>
                <Typography variant="subtitle2">{notification.subject}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>{notification.message}</Typography>
              </Stack>
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>No new notifications</MenuItem>
        )}
      </Menu>
    </>
  );
}